import { useEffect, useState } from 'react';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import { Onboarding } from '@ofself/shell';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import {
  joinNext, withNext, markFresh, heldPassword, holdPassword,
} from '../../utils/joinFlow';
import { countryOf, defaultCountry } from './countries';
import CountryField from './CountryField';
import JoinFrame from './JoinFrame';

// JOIN, THE PHONE (SIGNUP_MODE=phone) — the account approves itself by proving
// a phone. Arrived at from the first sign-in (Register, or Login for someone
// who left before this step), which answered PHONE_VERIFICATION_REQUIRED with
// a short-lived token, carried here in router state. The number, then the
// code; a right code signs the person in exactly as a sign-in does
// (AuthContext completeLogin), and the join goes on to the photo.
//
// The password typed at that sign-in is held in memory (joinFlow holdPassword)
// so the check can unlock the account's keys and mint its recovery codes. A
// reload keeps the token (router state) but loses the password, so without it
// the step sends the person back to sign in rather than finishing keyless.
//
// The channel is the server's to choose (a text today, WhatsApp later), so the
// copy says "a code" wherever it can.

const RESEND_AFTER = 30; // seconds

const SIGN_IN_AGAIN = {
  VERIFICATION_TOKEN_EXPIRED: 'This step timed out. Sign in again to finish creating your account.',
  VERIFICATION_TOKEN_INVALID: 'This step is no longer valid. Sign in again to finish creating your account.',
  ALREADY_APPROVED: 'Your account is already verified. Sign in to continue.',
  PHONE_SIGNUP_DISABLED: 'Phone verification is not needed here. Sign in to continue.',
};
const MISSING = 'To finish creating your account, sign in again.';
const NO_PASSWORD = 'For your security, sign in again to finish verifying your phone.';

const MESSAGES = {
  PHONE_INVALID: 'That doesn’t look like a mobile number. Check the country and the number.',
  PHONE_LINE_TYPE_NOT_ALLOWED: 'Virtual and internet (VoIP) numbers can’t be used. Please use a mobile number.',
  PHONE_IN_USE: 'That number is already used by another ofSelf account.',
  VERIFICATION_CODE_INVALID: 'That code isn’t right. Check it and try again, or send a new one.',
  VERIFICATION_RATE_LIMITED: 'Too many attempts. Please wait a few minutes and try again.',
  RATE_LIMIT_EXCEEDED: 'Too many attempts. Please wait a few minutes and try again.',
  SMS_UNAVAILABLE: 'We can’t send codes right now. Please try again shortly.',
  SERVICE_UNAVAILABLE: 'Phone verification isn’t available right now. Please try again shortly.',
  ACCOUNT_DISABLED: 'This account has been disabled. Please contact support.',
};
// A refusal of the number itself, met at the code: back to the number.
const ABOUT_THE_NUMBER = new Set(['PHONE_INVALID', 'PHONE_LINE_TYPE_NOT_ALLOWED', 'PHONE_IN_USE']);

const messageFor = (err) => MESSAGES[err?.code]
  || (err?.status === 429 ? MESSAGES.RATE_LIMIT_EXCEEDED : null)
  || err?.message
  || 'Something went wrong. Please try again.';

function JoinPhone() {
  const { completeLogin } = useAuth();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [searchParams] = useSearchParams();
  const next = joinNext(searchParams);
  const flow = state || {};
  const token = flow.verificationToken;

  const [country, setCountry] = useState(defaultCountry);
  const [number, setNumber] = useState('');
  const [sentTo, setSentTo] = useState(null); // { phone, country?, shown }
  const [code, setCode] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [busy, setBusy] = useState(false);
  const [lost, setLost] = useState(() => {
    if (!token) return MISSING;
    return heldPassword() ? null : NO_PASSWORD;
  });
  const [resendAt, setResendAt] = useState(0);
  const [now, setNow] = useState(() => Date.now());

  useEffect(() => {
    if (!resendAt) return undefined;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [resendAt]);
  const wait = Math.max(0, Math.ceil((resendAt - now) / 1000));

  const timedOut = () => !!flow.expiresAt && Date.now() > flow.expiresAt;

  const fail = (err) => {
    if (SIGN_IN_AGAIN[err?.code]) { setLost(SIGN_IN_AGAIN[err.code]); return; }
    if (sentTo && ABOUT_THE_NUMBER.has(err?.code)) { setSentTo(null); setCode(''); }
    setError(messageFor(err));
  };

  const sendTo = async (target) => {
    if (!heldPassword()) { setLost(NO_PASSWORD); return; }
    if (timedOut()) { setLost(SIGN_IN_AGAIN.VERIFICATION_TOKEN_EXPIRED); return; }
    setBusy(true);
    setError('');
    setNotice('');
    try {
      await api.phoneStart({ verificationToken: token, phone: target.phone, country: target.country });
      const resent = !!sentTo;
      setSentTo(target);
      setCode('');
      const at = Date.now();
      setNow(at);
      setResendAt(at + RESEND_AFTER * 1000);
      if (resent) setNotice('A new code is on its way.');
    } catch (err) {
      fail(err);
    } finally {
      setBusy(false);
    }
  };

  const send = (e) => {
    e.preventDefault();
    const typed = number.trim();
    if (typed.replace(/\D/g, '').length < 4) { setError('Enter your mobile number.'); return; }
    // A number typed with its own "+" carries its country; otherwise the picker's.
    const target = typed.startsWith('+')
      ? { phone: typed, shown: typed }
      : { phone: typed, country, shown: `+${countryOf(country).dial} ${typed}` };
    sendTo(target);
  };

  const verify = async (e) => {
    e.preventDefault();
    if (!/^\d{6}$/.test(code)) { setError('Enter the 6-digit code.'); return; }
    if (!heldPassword()) { setLost(NO_PASSWORD); return; }
    if (timedOut()) { setLost(SIGN_IN_AGAIN.VERIFICATION_TOKEN_EXPIRED); return; }
    setBusy(true);
    setError('');
    setNotice('');
    let res;
    try {
      res = await api.phoneCheck({
        verificationToken: token, phone: sentTo.phone, country: sentTo.country, code,
      });
      res = await completeLogin(res, heldPassword());
    } catch (err) {
      setBusy(false);
      fail(err);
      return;
    }
    holdPassword(null);
    if (flow.fresh) markFresh(res?.user?.id);
    // The name given at sign-up, which the first sign-in would have set.
    const profileId = res?.user?.default_profile_node_id;
    if (flow.name && profileId) {
      await api.updateNode(profileId, { title: flow.name, value_json: { display_name: flow.name } }).catch(() => {});
    }
    navigate(withNext('/join/photo', next), { replace: true });
  };

  const changeNumber = () => { setSentTo(null); setCode(''); setError(''); setNotice(''); };
  const errorLine = error && <p className="account-error" role="alert">{error}</p>;

  if (lost) {
    return (
      <JoinFrame>
        <Onboarding.Step title="Sign in to finish" subtitle={lost} centered>
          <Onboarding.Next onClick={() => navigate(withNext('/login', next), { replace: true })}>Go to sign in</Onboarding.Next>
        </Onboarding.Step>
      </JoinFrame>
    );
  }

  if (!sentTo) {
    const here = countryOf(country);
    const ownPrefix = number.trim().startsWith('+');
    return (
      <JoinFrame>
        <Onboarding.Step
          key="number"
          title="Verify your phone"
          subtitle="We’ll send a code to your mobile. One number per account, and it is never shown to anyone."
          centered
        >
          <form className="account-form" onSubmit={send} noValidate>
            <CountryField
              value={country}
              onChange={(iso) => { setError(''); setCountry(iso); }}
              disabled={ownPrefix}
            />
            <Onboarding.Fields>
              <Onboarding.Field
                label="mobile number"
                prefix={ownPrefix ? undefined : `+${here.dial}`}
                type="tel"
                inputMode="tel"
                autoComplete="tel-national"
                autoFocus
                maxLength={24}
                value={number}
                onChange={(e) => { setError(''); setNumber(e.target.value.replace(/[^\d+\s()-]/g, '')); }}
              />
            </Onboarding.Fields>
            {errorLine}
            <Onboarding.Next type="submit" disabled={busy} go={!busy}>
              {busy ? 'Sending…' : 'Send code'}
            </Onboarding.Next>
          </form>
        </Onboarding.Step>
      </JoinFrame>
    );
  }

  return (
    <JoinFrame>
      <Onboarding.Step
        key="code"
        title="Enter the code"
        subtitle={`We sent a 6-digit code to ${sentTo.shown}.`}
        back={{ label: 'Change number', onBack: changeNumber, disabled: busy }}
        centered
      >
        <form className="account-form" onSubmit={verify} noValidate>
          <Onboarding.Fields>
            <Onboarding.Field
              label="6-digit code"
              inputMode="numeric"
              autoComplete="one-time-code"
              autoFocus
              maxLength={6}
              value={code}
              onChange={(e) => { setError(''); setCode(e.target.value.replace(/\D/g, '').slice(0, 6)); }}
            />
          </Onboarding.Fields>
          {errorLine || (notice && <p className="account-status" role="status">{notice}</p>)}
          <button type="button" className="join-copy" onClick={() => sendTo(sentTo)} disabled={busy || wait > 0}>
            {wait > 0 ? `Resend code in ${wait}s` : 'Resend code'}
          </button>
          <Onboarding.Next type="submit" disabled={busy} go={!busy}>
            {busy ? 'Checking…' : 'Verify'}
          </Onboarding.Next>
        </form>
      </Onboarding.Step>
    </JoinFrame>
  );
}

export default JoinPhone;
